import React from 'react';
import { Link } from 'react-router-dom';
import Section from '../components/common/Section';
import CaseStudySection from '../components/sections/Cases/CaseStudySection';

/**
 * 特性卡片类型定义
 */
interface FeatureCard {
  id: string;
  title: string;
  description: string;
}

/**
 * 首页组件
 * @returns {JSX.Element} 渲染的React组件
 */
const HomePage: React.FC = () => {
  // 示例数据
  const featureCards: FeatureCard[] = [
    {
      id: '01',
      title: '智能数据分析',
      description: '利用AI技术自动分析您的业务数据，发现潜在问题和机会，帮助您做出更明智的决策。'
    },
    {
      id: '02',
      title: '多平台兼容',
      description: '我们的产品支持iOS、Android、Web多个平台，让您的用户在任何设备上都能获得一致的体验。'
    },
    {
      id: '03',
      title: '实时协作',
      description: '团队成员可以实时查看和编辑共享文档，提高工作效率，减少沟通成本。'
    },
    {
      id: '04',
      title: '安全加密',
      description: '采用先进的加密技术保护您的数据安全，防止未授权访问和数据泄露。'
    }
  ];

  // 案例研究数据
  const caseStudies = [
    {
      id: 'case1',
      title: '等爸爸西餐厅',
      description: '这款小程序提供了丰富的社交功能，包括一对一聊天、群聊、朋友圈等功能。用户可以通过文字、图片、语音等多种方式进行交流，支持表情包和贴纸，增强社交互动体验。',
      appScreenshot: {
        src: '/assets/images/products/dengbaba.png',
        alt: '聊天社交小程序截图'
      },
      tags: ['西餐', '餐饮', '朋友圈']
    }
  ];

  return (
    <>
      {/* 英雄区域 - 参考语雀设计风格 */}
      <Section
        id="hero"
        spacing="large"
        background="background"
      >
        <div className="flex flex-col lg:flex-row items-center justify-center max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16 lg:py-24">
          <div className="lg:w-1/2 lg:pr-8 mb-12 lg:mb-0 text-center lg:text-left">
            <div className="relative z-10 max-w-xl mx-auto lg:mx-0">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight scroll-fade-up">
                <span className="text-primary">佳达掌柜</span> 为您的业务提供智能解决方案
              </h1>
              <p className="text-base sm:text-lg text-gray-600 mb-6 md:mb-8 leading-relaxed scroll-fade-up scroll-delay-1">
                我们提供先进的AI技术解决方案，帮助您的业务实现数字化转型，提升效率和竞争力。从数据分析到智能决策，一站式满足您的需求。
              </p>
              <div className="flex flex-wrap gap-3 md:gap-4 justify-center lg:justify-start scroll-fade-up scroll-delay-2">
                <button
                  type="button"
                  className="bg-primary text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-medium hover:shadow-lg hover:-translate-y-1 transition-all duration-300 text-sm sm:text-base"
                >
                  免费体验
                </button>
                <button
                  type="button"
                  className="bg-white border border-gray-200 text-gray-800 px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-medium hover:border-primary hover:text-primary transition-all duration-300 text-sm sm:text-base"
                >
                  了解更多
                </button>
              </div>
              <div className="mt-6 md:mt-8 flex items-center text-xs sm:text-sm text-gray-500 justify-center lg:justify-start scroll-fade-up scroll-delay-3">
                <span>已有超过1000+企业选择我们的解决方案</span>
              </div>
            </div>
          </div>
          <div className="lg:w-1/2 relative w-full max-w-md mx-auto flex justify-center items-center">
            <div className="phone-mockup relative z-10 rounded-2xl overflow-hidden transform hover:-rotate-1 transition-transform duration-300 scroll-fade-up bg-background ml-0 lg:ml-8">
              <img 
                src="/assets/images/features/work.png" 
                alt="佳达掌柜平台界面" 
                className="max-h-[500px] md:max-h-[400px] sm:max-h-[350px] w-auto object-contain"
              />
            </div>
          </div>
        </div>
      </Section>

      {/* 核心优势展示区域 */}
      <section className="py-16 sm:py-20 md:py-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-background to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 md:mb-6 scroll-fade-up">为什么选择佳达掌柜</h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto scroll-fade-up scroll-delay-1">
              我们提供全方位的智能解决方案，帮助企业实现数字化转型，提升效率和竞争力
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16">
            {/* 优势卡片1 */}
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300 scroll-fade-up scroll-delay-1">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4 md:mb-6">
                <svg className="w-5 h-5 md:w-6 md:h-6 text-primary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"></path>
                </svg>
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">专业团队</h3>
              <p className="text-sm md:text-base text-gray-600">由行业专家组成的团队，拥有丰富的经验和专业知识，为您提供最佳解决方案</p>
            </div>
            
            {/* 优势卡片2 */}
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300 scroll-fade-up scroll-delay-2">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4 md:mb-6">
                <svg className="w-5 h-5 md:w-6 md:h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd"></path>
                </svg>
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">高效解决方案</h3>
              <p className="text-sm md:text-base text-gray-600">我们的解决方案经过精心设计，能够快速部署，帮助您的业务实现高效运转</p>
            </div>
            
            {/* 优势卡片3 */}
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300 scroll-fade-up scroll-delay-3">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-purple-50 rounded-xl flex items-center justify-center mb-4 md:mb-6">
                <svg className="w-5 h-5 md:w-6 md:h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"></path>
                </svg>
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">客户至上</h3>
              <p className="text-sm md:text-base text-gray-600">我们始终将客户需求放在首位，提供全方位的支持和服务，确保您的满意度</p>
            </div>
          </div>
        </div>
      </section>

      {/* 产品功能展示区域 */}
      <section className="py-24 bg-background relative overflow-hidden">
        <div className="absolute top-0 inset-x-0 h-32 bg-gradient-to-b from-white to-transparent"></div>
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-500/5 rounded-full filter blur-3xl"></div>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-primary/5 rounded-full filter blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 scroll-fade-up">强大的功能，简单的使用</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto scroll-fade-up scroll-delay-1">
              我们的产品提供丰富的功能，满足您的各种业务需求，让复杂的工作变得简单高效
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 feature-grid">
            {/* 特性卡片 - 使用更现代的设计 */}
            {featureCards.map((card, index) => (
              <div 
                key={card.id}
                className={`bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 scroll-fade-up scroll-delay-${index}`}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-primary/20 to-blue-500/20 rounded-2xl flex items-center justify-center mb-6 text-2xl">
                  {card.id}
                </div>
                <h3 className="text-xl font-bold mb-3">{card.title}</h3>
                <p className="text-gray-600 mb-6">{card.description}</p>
                <a 
                  href={`/features#feature-${card.id}`} 
                  className="inline-flex items-center text-primary font-medium hover:underline"
                >
                  <span>了解更多</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </a>
              </div>
            ))}
          </div>
          
          <div className="mt-16 text-center">
            <Link 
              to="/features" 
              className="inline-flex items-center justify-center bg-white border border-gray-200 text-primary px-8 py-4 rounded-xl font-medium hover:bg-primary/5 transition-all duration-300 shadow-sm"
            >
              <span>查看全部功能</span>
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
      
      {/* 客户案例展示区域 */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 scroll-fade-up">他们都在使用佳达掌柜</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto scroll-fade-up scroll-delay-1">
              来自各行各业的客户选择我们的解决方案，助力业务增长
            </p>
          </div>
          
          <div className="case-studies-grid">
            <CaseStudySection cases={caseStudies} />
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;